<!--
  Transmission Timeline Component
  Visual timeline of transmission attempts
  @component TransmissionTimeline
-->
<template>
  <div class="transmission-timeline">
    <q-timeline v-if="transmissions.length" color="primary">
      <q-timeline-entry
        v-for="transmission in transmissions"
        :key="transmission.id"
        :color="getStatusColor(transmission.status)"
        :icon="getStatusIcon(transmission.status)">
        
        <template #title>
          <div class="row items-center q-gutter-sm">
            <span class="text-weight-medium">
              {{ getTypeLabel(transmission.type) }}
            </span>
            <q-badge
              :color="getStatusColor(transmission.status)"
              :label="getStatusLabel(transmission.status)" />
          </div>
        </template>
        
        <template #subtitle>
          {{ formatDate(transmission.created_at) }}
        </template>
        
        <div class="q-mt-sm">
          <p v-if="transmission.error_log?.length" class="text-negative">
            {{ transmission.error_log[transmission.error_log.length - 1].error }}
          </p>
          <div class="row q-gutter-sm q-mt-sm">
            <q-btn
              v-if="transmission.status === 'failed' && interactive"
              :icon="symRoundedReplay"
              label="Retry"
              color="warning"
              size="sm"
              @click="$emit('retry', transmission)" />
            <q-btn
              v-if="transmission.error_log?.length && interactive"
              :icon="symRoundedError"
              label="View Error"
              color="negative"
              size="sm"
              @click="$emit('view-error', transmission)" />
          </div>
        </div>
      </q-timeline-entry>
    </q-timeline>
    
    <div v-else class="text-center q-pa-xl text-grey-6">
      <q-icon :name="symRoundedTimeline" size="4em" />
      <p class="q-mt-md">No transmission history available</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
    symRoundedCheckCircle,
    symRoundedError,
    symRoundedReplay,
    symRoundedSchedule,
    symRoundedTimeline
} from '@quasar/extras/material-symbols-rounded'
import { useI18n } from 'vue-i18n'
import type {
    TransactionTransmission,
    TransmissionStatus,
    TransmissionType
} from '../types'

interface Props {
  transmissions: TransactionTransmission[]
  loading?: boolean
  interactive?: boolean
}

interface Emits {
  retry: [transmission: TransactionTransmission]
  'view-error': [transmission: TransactionTransmission]
  cancel: [transmission: TransactionTransmission]
}

withDefaults(defineProps<Props>(), {
  loading: false,
  interactive: true
})

defineEmits<Emits>()

const { t } = useI18n()

const getStatusColor = (status: TransmissionStatus): string => {
  switch (status) {
    case 'success': return 'positive'
    case 'failed': return 'negative'
    case 'pending': return 'warning'
    case 'processing': return 'info'
    default: return 'grey'
  }
}

const getStatusIcon = (status: TransmissionStatus): string => {
  switch (status) {
    case 'success': return symRoundedCheckCircle
    case 'failed': return symRoundedError
    case 'pending': return symRoundedSchedule
    default: return symRoundedSchedule
  }
}

const getStatusLabel = (status: TransmissionStatus): string => {
  return status.charAt(0).toUpperCase() + status.slice(1)
}

const getTypeLabel = (type: TransmissionType): string => {
  return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString()
}
</script>

<style lang="scss" scoped>
.transmission-timeline {
  max-height: 600px;
  overflow-y: auto;
}
</style> 